import { useState } from 'react';
import { api, type CanonicalImportResult, type GoogleSheetImportResult } from '../lib/api.js';

function getSheetIdFromUrl(value: string): string {
  const match = value.match(/\/spreadsheets\/d\/([^/]+)/);
  return match?.[1] ?? '';
}

function getGidFromUrl(value: string): string {
  try {
    const url = new URL(value);
    return url.hash.match(/gid=(\d+)/)?.[1] ?? url.searchParams.get('gid') ?? '';
  } catch {
    return '';
  }
}

export function ImportsPage(): JSX.Element {
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [gid, setGid] = useState('0');
  const [csvUrl, setCsvUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GoogleSheetImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [canonicalCsv, setCanonicalCsv] = useState('');
  const [canonicalFileName, setCanonicalFileName] = useState('');
  const [canonicalResult, setCanonicalResult] = useState<CanonicalImportResult | null>(null);

  const sourceBody = (): { csv_url?: string; sheet_id?: string; gid?: string } => {
    const body: { csv_url?: string; sheet_id?: string; gid?: string } = {};
    if (csvUrl) {
      body.csv_url = csvUrl;
    } else {
      if (sheetId) body.sheet_id = sheetId;
      if (gid) body.gid = gid;
    }
    return body;
  };

  const previewImport = async (): Promise<void> => {
    setBusy(true);
    setProgress(null);
    setError(null);
    try {
      const next = await api.imports.googleSheet({ ...sourceBody(), dry_run: true });
      setResult(next);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const applyImport = async (approvedOnly: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      let offset = 0;
      let inserted = 0;
      let updated = 0;
      let databaseReviewRows = 0;
      const ambiguousRows: number[] = [];
      let latest: GoogleSheetImportResult | null = null;
      const base = sourceBody();
      do {
        const next = await api.imports.googleSheet({
          ...base,
          dry_run: false,
          approved_only: approvedOnly,
          apply_offset: offset,
          apply_limit: 100,
        });
        inserted += next.inserted;
        updated += next.updated;
        databaseReviewRows += next.database_review_rows;
        ambiguousRows.push(...next.ambiguous_rows);
        latest = {
          ...next,
          inserted,
          updated,
          database_review_rows: databaseReviewRows,
          ambiguous_rows: ambiguousRows,
        };
        setResult(latest);
        setProgress(`Applied ${Math.min((next.next_offset ?? next.eligible_rows), next.eligible_rows)} of ${next.eligible_rows} eligible rows...`);
        offset = next.next_offset ?? next.eligible_rows;
        if (next.done) break;
      } while (latest && latest.next_offset != null);
      setProgress(approvedOnly ? 'Approved-only import complete.' : 'Full import complete.');
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const exportCatalog = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const blob = await api.imports.exportCanonical();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'bpg-suppressor-catalog.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(String(err)); }
    finally { setBusy(false); }
  };

  const previewCanonical = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try { setCanonicalResult(await api.imports.canonical({ csv: canonicalCsv, dry_run: true })); }
    catch (err) { setError(String(err)); }
    finally { setBusy(false); }
  };

  const applyCanonical = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    let offset = 0;
    let inserted = 0;
    let updated = 0;
    let unchanged = 0;
    try {
      while (true) {
        const next = await api.imports.canonical({ csv: canonicalCsv, dry_run: false, apply_offset: offset, apply_limit: 100 });
        inserted += next.inserted;
        updated += next.updated;
        unchanged += next.unchanged;
        setCanonicalResult({ ...next, inserted, updated, unchanged });
        setProgress(`Processed ${next.next_offset ?? next.rows} of ${next.rows} rows…`);
        if (next.done || next.next_offset == null) break;
        offset = next.next_offset;
      }
      setProgress('Canonical import complete.');
    } catch (err) { setError(String(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <h1>Imports</h1>
      <p className="page-intro">
        Sync suppressor data from the saved BPG Google Sheet. Use Preview first to validate rows and
        see import issues, then Apply to upsert valid rows into the catalog.
      </p>

      {error && <div className="notice error">{error}</div>}

      <section className="panel">
        <h2>Canonical Catalog</h2>
        <p className="panel-description">
          Export the current application catalog, edit it in Excel or Google Sheets, then upload the same CSV for validation and re-import.
        </p>
        <div className="button-row">
          <button disabled={busy} onClick={() => void exportCatalog()}>Export Current Catalog</button>
        </div>
        <label className="file-field">Canonical CSV file
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setCanonicalFileName(file.name);
              setCanonicalResult(null);
              void file.text().then(setCanonicalCsv);
            }}
          />
        </label>
        {canonicalFileName && <p className="source-url">Selected: {canonicalFileName}</p>}
        <div className="button-row">
          <button disabled={busy || !canonicalCsv} onClick={() => void previewCanonical()}>Preview Canonical Import</button>
          <button className="primary" disabled={busy || !canonicalResult || canonicalResult.missing_record_ids.length > 0 || canonicalResult.ambiguous_rows.length > 0} onClick={() => void applyCanonical()}>
            Apply Canonical Import
          </button>
        </div>
      </section>

      {canonicalResult && (
        <section className="panel">
          <h2>{canonicalResult.dry_run ? 'Canonical Preview' : 'Canonical Import Results'}</h2>
          <div className="stat-grid">
            <div><span>Rows</span><strong>{canonicalResult.rows}</strong></div>
            <div><span>Insert</span><strong>{canonicalResult.inserted}</strong></div>
            <div><span>Update</span><strong>{canonicalResult.updated}</strong></div>
            <div><span>Unchanged</span><strong>{canonicalResult.unchanged}</strong></div>
            <div><span>Needs Review</span><strong>{canonicalResult.review_rows}</strong></div>
            <div><span>Issues</span><strong>{canonicalResult.issue_rows}</strong></div>
          </div>
          {canonicalResult.missing_record_ids.length > 0 && (
            <div className="notice error">Unknown record IDs: {canonicalResult.missing_record_ids.join(', ')}</div>
          )}
          {canonicalResult.ambiguous_rows.length > 0 && (
            <div className="notice error">Rows matching multiple existing records: {canonicalResult.ambiguous_rows.join(', ')}</div>
          )}
          {canonicalResult.issues.length > 0 && (
            <table>
              <thead><tr><th>Row</th><th>Record</th><th>Brand</th><th>Model</th><th>Issues</th></tr></thead>
              <tbody>{canonicalResult.issues.map((issue) => (
                <tr key={`${issue.row}-${issue.record_id ?? 'new'}`}>
                  <td>{issue.row}</td><td>{issue.record_id ?? 'New'}</td><td>{issue.brand ?? '—'}</td><td>{issue.model ?? '—'}</td>
                  <td style={{ color: 'var(--warning)' }}>{issue.issues.join('; ')}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section>
      )}

      <section className="panel">
        <h2>Google Sheet Import</h2>
        <div className="notice ok">
          The app uses the saved BPG size chart by default. Fill these fields only when you want to
          preview a different sheet or CSV export.
        </div>
        <div className="fields">
          <label className="full">Google Sheet URL
            <input
              value={sheetUrl}
              onChange={(e) => {
                const value = e.target.value;
                setSheetUrl(value);
                const parsedSheetId = getSheetIdFromUrl(value);
                const parsedGid = getGidFromUrl(value);
                if (parsedSheetId) setSheetId(parsedSheetId);
                if (parsedGid) setGid(parsedGid);
              }}
              placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0"
            />
          </label>
          <label>Sheet ID
            <input value={sheetId} onChange={(e) => setSheetId(e.target.value)} />
          </label>
          <label>Tab GID
            <input value={gid} onChange={(e) => setGid(e.target.value)} placeholder="0" />
          </label>
          <label className="full">Published CSV URL
            <input
              value={csvUrl}
              onChange={(e) => setCsvUrl(e.target.value)}
              placeholder="Optional: https://docs.google.com/spreadsheets/d/.../export?format=csv&gid=0"
            />
          </label>
        </div>

        <div className="button-row">
          <button disabled={busy} onClick={() => void previewImport()}>
            {busy ? 'Working…' : 'Preview'}
          </button>
          <button className="primary" disabled={busy || !result} onClick={() => void applyImport(true)}>
            Import Approved Only
          </button>
          <button disabled={busy || !result} onClick={() => void applyImport(false)}>
            Import All Rows
          </button>
        </div>
        <p className="panel-description">
          Approved-only import leaves flagged rows untouched. Import All Rows adds flagged records to the Needs Review queue.
        </p>
        {progress && <p className="import-progress">{progress}</p>}
      </section>

      {result && (
        <section className="panel">
          <h2>{result.dry_run ? 'Preview Results' : 'Import Results'}</h2>
          <div className="stat-grid">
            <div><span>Parsed</span><strong>{result.parsed_rows}</strong></div>
            <div><span>Importable</span><strong>{result.valid_rows}</strong></div>
            <div><span>Approved</span><strong>{result.approved_rows}</strong></div>
            <div><span>Needs Review</span><strong>{result.review_rows}</strong></div>
            <div><span>DB Conflicts</span><strong>{result.database_review_rows}</strong></div>
            <div><span>Issues</span><strong>{result.issue_rows}</strong></div>
            <div><span>Inserted</span><strong>{result.inserted}</strong></div>
            <div><span>Updated</span><strong>{result.updated}</strong></div>
          </div>
          <p className="source-url">Source: {result.source.csv_url}</p>
          {result.ambiguous_rows.length > 0 && (
            <p className="import-progress">
              Database conflicts were queued for review from sheet rows: {result.ambiguous_rows.join(', ')}
            </p>
          )}

          {result.issues.length > 0 && (
            <>
              <h2 style={{ marginTop: 20 }}>First 50 Issues</h2>
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Brand</th>
                    <th>Model</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {result.issues.map((issue) => (
                    <tr key={`${issue.row_index}-${issue.issues.join('|')}`}>
                      <td>{issue.row_index}</td>
                      <td>{issue.brand ?? '—'}</td>
                      <td>{issue.model ?? '—'}</td>
                      <td style={{ color: 'var(--warning)' }}>{issue.issues.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}
    </>
  );
}
